import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CardRecord, CardType, CardHistoryEntry, defaultDb } from '../db/indexedDb';

interface HistoryItem {
  card: CardRecord;
  entry: CardHistoryEntry;
  entryIndex: number;
}

type FilterType = 'all' | CardType;

const skillLabels: Record<CardType, string> = {
  speaking: '口语',
  writing: '写作',
  listening: '听力',
  vocab: '词汇',
};

const ratingColors: Record<string, string> = {
  again: 'var(--accent-red)',
  hard: 'var(--accent-yellow)',
  good: 'var(--accent-green)',
};

const getCardTitle = (card: CardRecord): string => {
  switch (card.type) {
    case 'speaking':
      return card.speakingMeta?.question || card.speakingMeta?.topic || card.id;
    case 'writing':
      // 话题默写：显示主题+子主题
      if (card.mode === 'topic-dictation') {
        const content = card.content as { majorTopic?: string; subTopic?: string } | undefined;
        if (content?.majorTopic || content?.subTopic) {
          return `${content.majorTopic || ''} ${content.subTopic || ''}`.trim();
        }
      }
      return card.writingMeta?.cnHint || (card.content as any)?.prompt || card.id;
    case 'listening':
      return card.listeningMeta?.transcript?.slice(0, 40) || card.id;
    case 'vocab':
      return card.vocabMeta?.en || card.vocabMeta?.front || card.id;
    default:
      return card.id;
  }
};

export default function HistoryPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');

  useEffect(() => {
    (async () => {
      setLoading(true);
      const cards = await defaultDb.cards.toArray();
      const allItems: HistoryItem[] = [];
      for (const card of cards) {
        for (let i = 0; i < card.history.length; i++) {
          allItems.push({ card, entry: card.history[i], entryIndex: i });
        }
      }
      allItems.sort((a, b) => b.entry.ts - a.entry.ts);
      setItems(allItems);
      setLoading(false);
    })();
  }, []);

  const filteredItems = filter === 'all' ? items : items.filter((item) => item.card.type === filter);

  // 按日期分组
  const groupedByDate = filteredItems.reduce<Record<string, HistoryItem[]>>((acc, item) => {
    const dateKey = new Date(item.entry.ts).toDateString();
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(item);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"><span></span></div>
      </div>
    );
  }

  const filters: { key: FilterType; label: string }[] = [
    { key: 'all', label: '全部' },
    { key: 'speaking', label: '口语' },
    { key: 'writing', label: '写作' },
    { key: 'listening', label: '听力' },
    { key: 'vocab', label: '词汇' },
  ];

  return (
    <div style={{ paddingBottom: '20px' }}>
      {/* Header with back */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0', marginBottom: '8px' }}>
        <button
          onClick={() => navigate('/stats')}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-primary)',
            fontSize: '18px',
            cursor: 'pointer',
            padding: '4px 8px',
          }}
        >
          ←
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>学习历史</h1>
          <span className="caption" style={{ color: 'var(--text-muted)' }}>
            {filteredItems.length} 条记录
          </span>
        </div>
      </div>

      {/* 紧凑筛选器 */}
      <div
        style={{
          display: 'flex',
          gap: '4px',
          marginBottom: '16px',
          borderBottom: '1px solid var(--border-color)',
          paddingBottom: '8px',
        }}
      >
        {filters.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            style={{
              background: filter === key ? 'var(--accent-cyan)' : 'transparent',
              color: filter === key ? '#000' : 'var(--text-secondary)',
              border: filter === key ? '2px solid var(--border-color)' : '2px solid transparent',
              padding: '6px 12px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* 历史列表 - 紧凑布局 */}
      {Object.entries(groupedByDate).length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
          暂无学习记录
        </div>
      ) : (
        Object.entries(groupedByDate).map(([dateKey, dateItems]) => {
          const date = new Date(dateKey);
          const now = new Date();
          const isToday = date.toDateString() === now.toDateString();
          const yesterday = new Date(now);
          yesterday.setDate(yesterday.getDate() - 1);
          const isYesterday = date.toDateString() === yesterday.toDateString();

          const dateLabel = isToday
            ? '今天'
            : isYesterday
              ? '昨天'
              : date.toLocaleDateString('zh-CN', { month: 'numeric', day: 'numeric' });

          return (
            <div key={dateKey} style={{ marginBottom: '16px' }}>
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '6px',
                }}
              >
                {dateLabel}
              </div>
              <div
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                }}
              >
                {dateItems.map((item, idx) => {
                  const title = getCardTitle(item.card);
                  const time = new Date(item.entry.ts).toLocaleTimeString('zh-CN', {
                    hour: '2-digit',
                    minute: '2-digit',
                  });

                  return (
                    <div
                      key={`${item.card.id}-${item.entryIndex}-${idx}`}
                      onClick={() => navigate(`/history/${item.card.id}/${item.entryIndex}`)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '12px 16px',
                        cursor: 'pointer',
                        borderBottom: idx < dateItems.length - 1 ? '1px solid var(--border-color)' : 'none',
                      }}
                    >
                      {/* 评级指示条 */}
                      <div
                        style={{
                          width: '3px',
                          height: '32px',
                          background: ratingColors[item.entry.rating] ?? 'var(--text-muted)',
                          flexShrink: 0,
                        }}
                      />
                      {/* 技能图标 */}
                      <span
                        className={`skill-dot ${item.card.type}`}
                        style={{ width: '6px', height: '6px', flexShrink: 0 }}
                      />
                      {/* 标题 */}
                      <div
                        style={{
                          flex: 1,
                          fontSize: '14px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {title}
                      </div>
                      {/* 时间 */}
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)', flexShrink: 0 }}>
                        {time}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
